import { Modal } from "react-bootstrap";
import dbtIcon from '@assets/images/dbt.png';
import { Pane, PaneContent } from "@pages/PageStructure.component";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import BuildOrchestrationORM, { BuildExecution, BuildOrchestration } from "@models/buildOrchestration";
import { invalidateEverything } from "@stores/data.store";
import toast from "@services/toast.service";
import { getErrorMessage } from "@services/errors.service";
import ConfettiExplosion from "react-confetti-explosion";
import PipelineOrchestration from "@components/orchestration/PipelineOrchestration.component";
import PliableLoader from "@components/loaders/PliableLoader.component";
import Danger from "@components/statusIndicators/Danger.component";
import SuccessAlert from "@components/statusIndicators/SuccessAlert.component";
import { Link } from "react-router-dom";
import { PipelineNode } from "@models/pipelineNode";
import { runBuild } from "./alert.service";
import { emit } from "@nextcloud/event-bus";

/**
 * Returns a random whimsical loading message for Pliable's loading screen.
 * These messages highlight the complex data processing happening behind the scenes.
 * @returns A random loading message string
 */
export function getRandomLoadingMessage(): string {
    const loadingMessages: string[] = [
        "Looking under rocks for your data...",
        "Performing data wizardry that would impress your entire engineering team...",
        "Compressing hours of data engineering into milliseconds...",
        "Teaching machines to do what used to require three caffeinated humans...",
        "Bending the laws of data physics so you don't have to...",
        "Negotiating peace treaties between conflicting datasets...",
        "Folding space-time continuum (and your data) into meaningful insights...",
        "Turning what used to be a three-person job into your coffee break...",
        "Persuading stubborn data points to reveal their secrets...",
        "Doing in seconds what your last company needed a whole team for...",
        "Making big data feel small and manageable...",
        "Converting data chaos into beautiful order...",
        "Saving you thousands of dollars in data engineering salaries...",
    ];
  
    // Generate a random index within the array length
    const randomIndex = Math.floor(Math.random() * loadingMessages.length);
    
    // Return the message at the random index
    return loadingMessages[randomIndex];
};



export interface BuildModalProps {
    orchestrationId: string;
    show: boolean;
    onClose: () => void;

    completeButtons?: ReactNode;
    errorButtons?: ReactNode;

    completeNextButtonText?: string;
    onClickNextComplete?: () => void;
    errorNextButtonText?: string;
    onComplete?: () => void;
    onFailure?: (err: string) => void;

    compactMode?: boolean;
    compactModeCompleteNextButtonText?: string;
    compactModeBuildingCopy?: string;
    compactModeOnClickViewData?: () => void;
    compactModeOnClickNext?: () => void;
    compactModeErrorNextButtonText?: string;
    compactModeOnClickNextError?: () => void;
    skipInvalidate?: boolean;

    nonTechnical?: boolean;
}

const BuildModal = (props: BuildModalProps) => {
    const [activeOrchestration, setActiveOrchestration] = useState<BuildOrchestration|undefined>(undefined);
    const [showConfetti, setShowConfetti] = useState(false);
    const checkInitialOrchestration = useCallback(async () => {
        const updatedOrch = await BuildOrchestrationORM.findById(props.orchestrationId);
        setActiveOrchestration(updatedOrch);
        setShowConfetti(false);
    }, [props.orchestrationId]);

    const loadingMessage = useMemo(() => {
        return getRandomLoadingMessage();
    }, [props.orchestrationId]);

    useEffect(() => {
        checkInitialOrchestration();
    }, [props.orchestrationId, checkInitialOrchestration])
    
    const checkActiveOrchestration = useCallback(async () => {
        if (!activeOrchestration) {
            return;
        }

        const newStatuses: {
            [key: string]: BuildExecution
        } = {};
    
        Object.keys(activeOrchestration.build_executions).map(k => {
            const be = activeOrchestration.build_executions[k];
            newStatuses[`${be.object_type}:${be.object_id}`] = be;
        });

        
        if (!['ERROR', 'COMPLETE', 'IN_REVIEW', 'CANCELLED'].includes(activeOrchestration.status)) {
            setTimeout(async () => {
                // Keep polling, which should then re-run this effect, and so on.
                const updatedOrch = await BuildOrchestrationORM.findById(activeOrchestration.id as string);
                setActiveOrchestration(updatedOrch);
            }, 1000);
        } else if (activeOrchestration.status == 'COMPLETE') {
            setShowConfetti(true);
            setTimeout(() => {
                setShowConfetti(false);
            }, 1000);

            if (!props.skipInvalidate) {
                invalidateEverything();
            }
            props.onComplete?.();
        } else if (activeOrchestration.status == 'ERROR') {
            toast('danger', 'Error', getErrorMessage(activeOrchestration.error));
            props.onFailure && props.onFailure(activeOrchestration.error as string);
        }

    }, [activeOrchestration]);

    useEffect(() => {
        if (activeOrchestration) {
            checkActiveOrchestration();
        }
    }, [activeOrchestration]);


    const cancelActiveOrchestration = useCallback(async () => {
        if (!activeOrchestration) {
            return;
        }

        const updatedOrch = await BuildOrchestrationORM.cancelOrchestration(activeOrchestration.id as string);

    }, [activeOrchestration]);

    return <Modal size={props.compactMode ? undefined : 'lg'} show={props.show} onHide={props.onClose} backdrop="static">
    <Modal.Header>
        <Modal.Title>
            Refreshing data...
        </Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-0" style={{height: props.compactMode ? ' calc(40vh)': 'calc(70vh)'}}>
            <Pane>
                <PaneContent className="noscroll-h">
                    {showConfetti && <div style={{marginLeft: '50%'}}> <ConfettiExplosion zIndex={1061} colors={['#ff9f00', '#ffffff', '#000000', '#313A46', "#666666", "#343434"]} /></div>}

                    <div className="p-2">
                        {props.compactMode && <>
                            {activeOrchestration && ['RUNNING', 'QUEUED'].includes(activeOrchestration.status) && <>
                                <p>{props.compactModeBuildingCopy}</p>
                                <PliableLoader/>
                            </>}
                            {activeOrchestration && activeOrchestration.status == 'COMPLETE' && <>
                                <SuccessAlert>
                                    <div>Your data has been processed and is ready to use.</div>
                                </SuccessAlert>
                                <button className="btn btn-light me-1" onClick={() => {
                                    props.onClose();
                                    props.compactModeOnClickViewData?.();
                                }}>View Data</button>
                                {props.compactModeOnClickNext && <button className="btn btn-success" onClick={() => {
                                    props.onClose();
                                    props.compactModeOnClickNext?.();
                                }}>{props.compactModeCompleteNextButtonText}</button>}
                            </>}
                            {activeOrchestration && activeOrchestration.status == 'ERROR' && <>
                                <Danger>
                                    <div><div>There was an error processing your data.</div>
                                    <div>{activeOrchestration.error}</div></div>
                                    {props.compactModeOnClickNextError && <button className="btn btn-light" onClick={() => {
                                        props.onClose();
                                        props.compactModeOnClickNextError?.();
                                    }}>{props.compactModeErrorNextButtonText || 'Fix Error'}</button>}
                                </Danger>
                            </>}
                            {activeOrchestration && activeOrchestration.status == 'CANCELLED' && <>
                                <Danger>
                                    <div><div>This build was cancelled.</div>
                                    <div>{activeOrchestration.error}</div></div>
                                </Danger>
                            </>}
                        </>}
                        {!props.compactMode && !props.nonTechnical &&<>
                            {activeOrchestration && <PipelineOrchestration orchestration={activeOrchestration}/>}
                        </>}
                        {!props.compactMode && !!props.nonTechnical && <>
                            <div style={{margin: 'auto', width: '150px'}}>
                                <PliableLoader size="lg"></PliableLoader>
                            </div>
                            <div className="text-center">
                                <h3 className="mb-0 fw-bold">{loadingMessage}</h3>

                            </div>
                        </>}
                        

                    </div>

                </PaneContent>
            </Pane>
        
        
    </Modal.Body>
    <Modal.Footer>
        {['RUNNING', 'QUEUED'].includes(activeOrchestration?.status as string) && <>
            <button onClick={() => {
                cancelActiveOrchestration();
                // props.onClose();
            }} className="btn btn-light">Cancel Build</button>
            
        </>}
        {activeOrchestration?.status == 'COMPLETE' && <>
            {props.completeButtons ? props.completeButtons : <>
                <button onClick={() => {
                    props.onClose();
                }} className="btn btn-light">Close</button>
            </>}
        </>}
        {activeOrchestration?.status == 'ERROR' && <>
            {props.errorButtons ? props.errorButtons : <>
                <button onClick={() => {
                    props.onClose();
                }} className="btn btn-light">Close</button>
            </>}
        </>}
        {activeOrchestration?.status == 'CANCELLED' && <>
            <button onClick={() => {
                    props.onClose();
                }} className="btn btn-light">Close</button>
        </>}
        
    </Modal.Footer>
</Modal>
}

export default BuildModal;

export const triggerBuild = async (
    node: PipelineNode, 
    selector: 'THIS' | 'UPSTREAM' | 'DOWNSTREAM' | 'ALL', 
    force: boolean = false, 
    checkForNewRecords: boolean = false,
    onViewData: () => any,
) => {
    try {
        let selectorValue: string = '';
        switch (selector) {
            case 'THIS':
                selectorValue = node.name;
                break;
            case 'UPSTREAM':
                selectorValue = '+' + node.name;
                break;
            case 'DOWNSTREAM':
                selectorValue = node.name + '+';
                break;
            case 'ALL':
                selectorValue = '+' + node.name + '+'
                break;
        }
        await runBuild(
            selectorValue, 
            force,
            checkForNewRecords,
            <>
                <button onClick={() => {
                    onViewData();
                    emit('pliable:alerts:closebuild', {});
                }} className="btn btn-primary me-1">View Data</button>
                <button onClick={() => {
                    emit('pliable:alerts:closebuild', {});
                }} className="btn btn-light">Continue Editing</button>
            </>,
            <>
                <button onClick={() => {
                    emit('pliable:alerts:closebuild', {});
                }} className="btn btn-light">Continue Editing</button>
            </>
        );


    } catch (err) {
        toast('danger', 'Error', getErrorMessage(err));
    }
}